import React, { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { fetchLinkPreview, getCachedLinkPreview } from "@/lib/linkPreviewCache";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { embedLabel, splitUrl } from "@/lib/linkText";
import { useGuard } from "@/lib/guards";

// Lot 667 — aperçu riche d'un lien collé dans un commentaire (ou une publication sans carte enregistrée) :
// récupéré à l'affichage via GET /api/link-preview. Rien si le lien ne donne ni titre ni image (évite une carte vide).
// Lot 678 — allégé : cache mémoire + appareil (24 h) lu de façon synchrone au premier rendu, 2 requêtes max en
// parallèle (lib/linkPreviewCache.js) ; la publication n'est de toute façon montée que près de l'écran (fil fenêtré 677).
const usable = (d) => (d && (d.title || d.thumbnail) ? d : null);
export const useLinkPreview = (rawUrl) => {
  const on = useGuard("link_previews"); // lot 671 — garde-fou admin : coupé → aucune requête d'aperçu à l'affichage
  const url = on && rawUrl ? splitUrl(rawUrl).url : null;
  const [pv, setPv] = useState(() => (url ? usable(getCachedLinkPreview(url)) : null));
  useEffect(() => {
    let alive = true;
    if (!url) { setPv(null); return undefined; }
    const hit = getCachedLinkPreview(url);
    if (hit !== undefined) { setPv(usable(hit)); return undefined; } // déjà connu : aucune requête
    fetchLinkPreview(url).then((d) => { if (alive) setPv(usable(d)); });
    return () => { alive = false; };
  }, [url]);
  return pv;
};

export const LinkCard = ({ url, testId = "link-card", compact = false }) => {
  const pv = useLinkPreview(url);
  if (!pv) return null;
  const isVideo = pv.kind !== "article";
  const thumb = pv.thumbnail ? resolveMediaUrl(pv.thumbnail) : null;
  return (
    <a
      href={pv.url || url}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(e) => e.stopPropagation()}
      className={`block ${compact ? "max-w-xs" : "max-w-md"} mt-1.5 rounded-xl overflow-hidden border transition-opacity hover:opacity-90`}
      style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }}
      data-testid={testId}
    >
      {thumb && (
        <div className="relative">
          <img src={thumb} alt="" loading="lazy" decoding="async" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} className={`w-full ${compact ? "max-h-36" : "max-h-56"} object-cover`} data-testid={`${testId}-image`} />
          {isVideo && (
            <span className="absolute inset-0 grid place-items-center pointer-events-none">
              <span className="h-10 w-10 rounded-full bg-black/55 grid place-items-center"><Play size={18} className="text-white ml-0.5" fill="currentColor" /></span>
            </span>
          )}
        </div>
      )}
      <div className="px-3 py-2">
        <div className="text-[10px] font-semibold uppercase tracking-wide truncate" style={{ color: "var(--yalola-text-muted)" }}>{embedLabel(pv)}</div>
        <div className="text-[13px] font-semibold leading-snug line-clamp-2" style={{ color: "var(--yalola-text)", overflowWrap: "anywhere" }} data-testid={`${testId}-title`}>{pv.title || url}</div>
        {(pv.description || pv.author) && <div className="text-[11px] leading-snug line-clamp-2 mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{pv.description || pv.author}</div>}
      </div>
    </a>
  );
};

export default LinkCard;
